/**
 * Dynamic Listing Block Frontend Script
 * Handles filter interactions and AJAX filtering
 */

class DynamicListing {
    constructor(element) {
        this.element = element;
        this.listingType = element.dataset.listingType || 'properties';
        this.filterLayout = element.dataset.filterLayout || 'top';
        this.filterStyle = element.dataset.filterStyle || 'dropdown';
        this.filtersContainer = element.querySelector('.dynamic-listing-filters');
        this.contentContainer = element.querySelector('.dynamic-listing-content');
        this.toggleButton = element.querySelector('.dynamic-listing-filter-toggle');
        this.filterControls = element.querySelectorAll('.filter-control');
        
        this.init();
    }

    init() {
        // Toggle button for hidden layout
        if (this.toggleButton) {
            this.toggleButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleFilters();
            });
            
            // Ensure filters are hidden initially if layout is hidden
            if (this.filterLayout === 'hidden' && this.filtersContainer) {
                const computedStyle = window.getComputedStyle(this.filtersContainer);
                if (computedStyle.display !== 'none') {
                    this.filtersContainer.style.display = 'none';
                }
            }
        }

        // Filter controls event listeners
        this.filterControls.forEach(control => {
            if (control.tagName === 'SELECT') {
                control.addEventListener('change', () => this.applyFilters());
            } else if (control.type === 'checkbox') {
                control.addEventListener('change', () => this.applyFilters());
            } else if (control.classList.contains('filter-button')) {
                control.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.handleButtonFilter(control);
                });
            }
        });

        // Clear filters button
        const clearButton = this.element.querySelector('.clear-filters-button');
        if (clearButton) {
            clearButton.addEventListener('click', () => this.clearFilters());
            // Show/hide clear button based on active filters
            this.updateClearButton();
        }
    }

    toggleFilters() {
        if (this.filtersContainer && this.toggleButton) {
            const isHidden = this.filtersContainer.style.display === 'none' || 
                           this.filtersContainer.style.display === '' ||
                           window.getComputedStyle(this.filtersContainer).display === 'none';
            
            // Toggle visibility
            this.filtersContainer.style.display = isHidden ? 'block' : 'none';
            
            // Update button state
            this.toggleButton.classList.toggle('active', isHidden);
            this.toggleButton.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
            
            // Update button text
            const toggleText = this.toggleButton.querySelector('.toggle-text');
            if (toggleText) {
                toggleText.textContent = isHidden ? 
                    this.toggleButton.dataset.hideText || 'Hide Filters' : 
                    this.toggleButton.dataset.showText || 'Show Filters';
            }
            
            // Update toggle icon
            const toggleIcon = this.toggleButton.querySelector('.toggle-icon');
            if (toggleIcon) {
                toggleIcon.textContent = isHidden ? '▲' : '▼';
            }
        }
    }

    handleButtonFilter(button) {
        const group = button.closest('.filter-group');
        const buttons = group.querySelectorAll('.filter-button');
        
        // Remove active class from all buttons in group
        buttons.forEach(btn => btn.classList.remove('active'));
        
        // Add active class to clicked button
        button.classList.add('active');
        
        this.applyFilters();
    }

    getActiveFilters() {
        const filters = {
            type: [],
            status: [],
            developer: [],
            amenities: [],
            features: []
        };

        this.filterControls.forEach(control => {
            if (control.classList.contains('filter-type')) {
                if (control.tagName === 'SELECT' && control.value) {
                    filters.type.push(control.value);
                } else if (control.type === 'checkbox' && control.checked) {
                    filters.type.push(control.value);
                } else if (control.classList.contains('filter-button') && control.classList.contains('active')) {
                    const value = control.dataset.value;
                    if (value) filters.type.push(value);
                }
            } else if (control.classList.contains('filter-status')) {
                if (control.tagName === 'SELECT' && control.value) {
                    filters.status.push(control.value);
                } else if (control.type === 'checkbox' && control.checked) {
                    filters.status.push(control.value);
                } else if (control.classList.contains('filter-button') && control.classList.contains('active')) {
                    const value = control.dataset.value;
                    if (value) filters.status.push(value);
                }
            } else if (control.classList.contains('filter-developer')) {
                if (control.tagName === 'SELECT' && control.value) {
                    filters.developer.push(control.value);
                } else if (control.type === 'checkbox' && control.checked) {
                    filters.developer.push(control.value);
                } else if (control.classList.contains('filter-button') && control.classList.contains('active')) {
                    const value = control.dataset.value;
                    if (value) filters.developer.push(value);
                }
            } else if (control.classList.contains('filter-amenities')) {
                if (control.tagName === 'SELECT') {
                    Array.from(control.selectedOptions).forEach(option => {
                        if (option.value) filters.amenities.push(option.value);
                    });
                } else if (control.type === 'checkbox' && control.checked) {
                    filters.amenities.push(control.value);
                } else if (control.classList.contains('filter-button') && control.classList.contains('active')) {
                    const value = control.dataset.value;
                    if (value) filters.amenities.push(value);
                }
            } else if (control.classList.contains('filter-features')) {
                if (control.tagName === 'SELECT') {
                    Array.from(control.selectedOptions).forEach(option => {
                        if (option.value) filters.features.push(option.value);
                    });
                } else if (control.type === 'checkbox' && control.checked) {
                    filters.features.push(control.value);
                } else if (control.classList.contains('filter-button') && control.classList.contains('active')) {
                    const value = control.dataset.value;
                    if (value) filters.features.push(value);
                }
            }
        });

        return filters;
    }

    applyFilters() {
        const filters = this.getActiveFilters();
        
        // Filter the displayed items based on selected filters
        const cards = this.contentContainer.querySelectorAll('.listing-card');
        let visibleCount = 0;
        
        cards.forEach(card => {
            let show = true;
            
            // Check type filter (for properties)
            if (filters.type.length > 0 && this.listingType === 'properties') {
                const cardTypes = card.dataset.types ? card.dataset.types.split(',') : [];
                if (!filters.type.some(type => cardTypes.includes(type))) {
                    show = false;
                }
            }
            
            // Check status filter
            if (filters.status.length > 0) {
                const cardStatuses = card.dataset.statuses ? card.dataset.statuses.split(',') : [];
                if (!filters.status.some(status => cardStatuses.includes(status))) {
                    show = false;
                }
            }
            
            // Check developer filter
            if (filters.developer.length > 0) {
                const cardDevelopers = card.dataset.developers ? card.dataset.developers.split(',') : [];
                if (!filters.developer.some(dev => cardDevelopers.includes(dev))) {
                    show = false;
                }
            }
            
            // Check amenities filter (for properties)
            if (filters.amenities.length > 0 && this.listingType === 'properties') {
                const cardAmenities = card.dataset.amenities ? card.dataset.amenities.split(',') : [];
                if (!filters.amenities.some(amenity => cardAmenities.includes(amenity))) {
                    show = false;
                }
            }
            
            // Check features filter (for properties)
            if (filters.features.length > 0 && this.listingType === 'properties') {
                const cardFeatures = card.dataset.features ? card.dataset.features.split(',') : [];
                if (!filters.features.some(feature => cardFeatures.includes(feature))) {
                    show = false;
                }
            }
            
            card.style.display = show ? '' : 'none';
            if (show) visibleCount++;
        });

        // Update clear button visibility
        this.updateClearButton();
    }

    updateClearButton() {
        const clearButton = this.element.querySelector('.clear-filters-button');
        if (!clearButton) return;

        const filters = this.getActiveFilters();
        const hasActiveFilters = 
            filters.type.length > 0 ||
            filters.status.length > 0 ||
            filters.developer.length > 0 ||
            filters.amenities.length > 0 ||
            filters.features.length > 0;

        clearButton.style.display = hasActiveFilters ? 'block' : 'none';
    }

    clearFilters() {
        this.filterControls.forEach(control => {
            if (control.tagName === 'SELECT') {
                if (control.multiple) {
                    Array.from(control.options).forEach(option => option.selected = false);
                } else {
                    control.value = '';
                }
            } else if (control.type === 'checkbox') {
                control.checked = false;
            } else if (control.classList.contains('filter-button')) {
                const group = control.closest('.filter-group');
                if (group) {
                    const buttons = group.querySelectorAll('.filter-button');
                    buttons.forEach(btn => btn.classList.remove('active'));
                    const allButton = group.querySelector('.filter-button[data-value=""]');
                    if (allButton) {
                        allButton.classList.add('active');
                    }
                }
            }
        });
        
        this.applyFilters();
    }
}

// Initialize all dynamic listings
function initDynamicListings() {
    const listings = document.querySelectorAll('.block-dynamic-listing');
    listings.forEach(listing => {
        new DynamicListing(listing);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDynamicListings);
} else {
    initDynamicListings();
}
